import {ListItemIcon, Menu, Stack, Typography, useTheme} from '@mui/material';
import {HelpPageTaggingEnum, ServicesTaggingEnum} from 'main/analytics/gaTagsEnum';
import {IconIllustration} from 'presentation/components/IconIllustration';
import {
	FeaturesEnum,
	HelpCenterEnum,
	HomeEnum,
	ProfileEnum,
	SelectionFlowEnum,
} from 'presentation/constants';
import {useContractStatus, useHostName} from 'presentation/hooks';
import Icon from 'presentation/icons';
import {planTypeOptions, useAccountInformation, useMediaQueries} from 'presentation/pages';
import {translations} from 'presentation/translations-new';
import {formatText, getInitials, getOnlyFirstWord} from 'presentation/utils';
import React, {Fragment, MouseEvent, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {MainHeader} from '..';
import {ProfileMenuEnum} from '../../../../../tests/constants/ProfileMenuEnum';
import {StyledMenuItemBeneficiary, StyledMenuItemLogout} from '../styles';
import {BeneficiaryInfoLegacy} from './components/BeneficiaryInfo/BeneficiaryInfoLegacy';
import {desktopPaperSx, mobilePaperSx} from './components/constants';
import {
	ArrowIcon,
	ProfileContainerLegacy,
	ProfileIconLegacy,
	StyledBeneficiaryNameLegacy,
	StyledBox,
	StyledInitialsLegacy,
	StyledMenuItemLegacy,
	StyledPlanName,
} from './styles';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';

const {changePlanBeneficiary, logOut, selectedBeneficiary, myProfile, help} =
	translations['pt-br'].others.profileMenu;

export function ProfileMenuLegacy() {
	const [anchorElProfile, setAnchorElProfile] = useState<null | HTMLElement>(null);
	const {beneficiary, isB2BAccess, isResponsibleWithoutBenefits, handleLogout} =
		useAccountInformation();
	const {isValid, isCancelledAtOneYear} = useContractStatus();
	const {isGndiSaoPaulo} = useHostName();
	const {isMobile} = useMediaQueries();
	const theme = useTheme();
	const navigate = useNavigate();
	const {pathname} = useLocation();

	const {isEnabled: isMultiThemeFeatureFlagEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_NDI_MULTI_THEME,
	);

	const SHOW_HELP = (isValid && !isB2BAccess) || (isCancelledAtOneYear && !isB2BAccess);

	const isHomePage = pathname === HomeEnum.HOME_PAGE;

	const beneficiaryNameFormatted = getOnlyFirstWord(
		formatText(beneficiary?.nmSocialUsuarioC || beneficiary?.nmUsuarioC),
	);

	const handleOpenProfileMenu = (event: MouseEvent<HTMLElement>) => {
		setAnchorElProfile(event.currentTarget);
	};

	const handleCloseProfileMenu = () => {
		setAnchorElProfile(null);
	};

	const handleChangeAccount = () => {
		navigate(SelectionFlowEnum.PLAN_SELECTION);
		setAnchorElProfile(null);
	};

	const handleGoToProfile = () => {
		navigate(ProfileEnum.PROFILE);
		setAnchorElProfile(null);
	};

	const handleGoToHelpCenter = () => {
		navigate(HelpCenterEnum.HELP);
		setAnchorElProfile(null);
	};

	return (
		<Fragment>
			<ProfileContainerLegacy
				size="small"
				onClick={handleOpenProfileMenu}
				data-testid="profile-menu-button"
				$isHomePage={isHomePage}>
				<ProfileIconLegacy alt="Profile Avatar" $isHomePage={isHomePage}>
					<StyledInitialsLegacy color="primary" $isHomePage={isHomePage}>
						{getInitials(beneficiary?.nmSocialUsuarioC || beneficiary?.nmUsuarioC)}
					</StyledInitialsLegacy>
				</ProfileIconLegacy>
				<StyledBox textAlign="start" mx={1} color="white" $isGndiSaoPaulo={isGndiSaoPaulo}>
					<StyledBeneficiaryNameLegacy variant="subtitle2" $isHomePage={isHomePage}>
						{beneficiaryNameFormatted}
					</StyledBeneficiaryNameLegacy>
					<StyledPlanName variant="body2" $isHomePage={isHomePage}>
						{planTypeOptions?.[beneficiary?.tipoPlanoC]?.label}
					</StyledPlanName>
				</StyledBox>
				<ArrowIcon
					name={
						isMultiThemeFeatureFlagEnabled
							? 'chevron-right-outlined'
							: 'arrow-down-filled'
					}
					size={20}
					$isHomePage={isHomePage}
				/>
			</ProfileContainerLegacy>
			{anchorElProfile ? (
				<Menu
					anchorEl={anchorElProfile}
					anchorOrigin={{
						vertical: 'top',
						horizontal: 'right',
					}}
					keepMounted
					transformOrigin={{
						vertical: 'top',
						horizontal: 'right',
					}}
					open={Boolean(anchorElProfile)}
					onClose={handleCloseProfileMenu}
					slotProps={{
						list: {disablePadding: true},
						paper: {
							sx: isMobile ? mobilePaperSx : desktopPaperSx,
						},
					}}>
					{isMobile ? (
						<MainHeader
							title={selectedBeneficiary}
							handleBack={handleCloseProfileMenu}
							hideProfileMenu={true}
						/>
					) : null}
					<StyledMenuItemBeneficiary
						disabled={!isMobile}
						divider={!isMobile}
						disableGutters>
						<BeneficiaryInfoLegacy
							isResponsibleWithoutBenefits={isResponsibleWithoutBenefits}
							hasSocialNameToolTip={isMobile}
						/>
					</StyledMenuItemBeneficiary>

					<StyledMenuItemLegacy divider onClick={handleChangeAccount}>
						<Stack
							flex={1}
							direction="row"
							alignItems="center"
							justifyContent="space-between"
							spacing={1}
							data-testid={ProfileMenuEnum.CHANGE_PLAN_OR_BENEFICIARY}>
							<Stack spacing={1} direction="row" alignItems="center">
								<IconIllustration
									customIconName="group-filled"
									variant="primary"
									size="smaller"
								/>
								<Typography variant="body2"> {changePlanBeneficiary}</Typography>
							</Stack>
							<Icon color={theme.palette.primary.main} name="arrow-right-filled" />
						</Stack>
					</StyledMenuItemLegacy>
					{!isB2BAccess ? (
						<StyledMenuItemLegacy
							data-tag-id={ServicesTaggingEnum.CLICK_MEU_PERFIL}
							divider
							onClick={handleGoToProfile}
							data-testid={ProfileMenuEnum.MY_PROFILE}>
							<Stack
								flex={1}
								direction="row"
								alignItems="center"
								justifyContent="space-between">
								<Stack spacing={1} direction="row" alignItems="center">
									<IconIllustration
										customIconName="person-filled"
										variant="primary"
										size="smaller"
									/>
									<Typography variant="body2">{myProfile}</Typography>
								</Stack>
								<Icon
									color={theme.palette.primary.main}
									name="arrow-right-filled"
								/>
							</Stack>
						</StyledMenuItemLegacy>
					) : null}
					{SHOW_HELP ? (
						<StyledMenuItemLegacy
							divider
							onClick={handleGoToHelpCenter}
							data-tagid={HelpPageTaggingEnum.CLICK_ACESSAR_AJUDA}>
							<Stack
								flex={1}
								direction="row"
								alignItems="center"
								justifyContent="space-between"
								data-testid={ProfileMenuEnum.HELP}>
								<Stack spacing={1} direction="row" alignItems="center">
									<IconIllustration
										customIconName="live-help-filled"
										variant="primary"
										size="smaller"
									/>
									<Typography variant="body2"> {help}</Typography>
								</Stack>
								<Icon
									color={theme.palette.primary.main}
									name="arrow-right-filled"
								/>
							</Stack>
						</StyledMenuItemLegacy>
					) : null}
					<StyledMenuItemLogout
						data-test="logout-button"
						data-testid={ProfileMenuEnum.LOGOUT_BUTTON}
						onClick={handleLogout}>
						<ListItemIcon>
							<Icon name="exit-to-app-filled" color={theme.palette.error.main} />
						</ListItemIcon>
						<Typography variant="body2" color={theme.palette.error.main}>
							{logOut}
						</Typography>
					</StyledMenuItemLogout>
				</Menu>
			) : null}
		</Fragment>
	);
}
